import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 活動追跡ストア
 *
 * セッションIDをlocalStorageに永続化し、
 * ページリロード後も同一セッションとして追跡を継続する
 */

interface ActivityState {
  // 現在のセッションID（null = セッション未開始）
  sessionId: string | null;

  // セッション開始時刻
  sessionStartedAt: string | null;

  // 最後のページパス（遷移元として使用）
  lastPagePath: string | null;

  // 最後のアクティビティ時刻
  lastActivityAt: string | null;

  // Actions
  setSessionId: (sessionId: string) => void;
  updateLastPage: (pagePath: string) => void;
  updateLastActivity: () => void;
  clearSession: () => void;
}

/**
 * セッションの有効期限をチェック
 * 8時間以上経過した場合はセッションを無効とする
 */
const isSessionExpired = (sessionStartedAt: string | null): boolean => {
  if (!sessionStartedAt) return true;

  const startTime = new Date(sessionStartedAt).getTime();
  const now = Date.now();
  const eightHours = 8 * 60 * 60 * 1000;

  return now - startTime > eightHours;
};

export const useActivityStore = create<ActivityState>()(
  persist(
    (set, _get) => ({
      sessionId: null,
      sessionStartedAt: null,
      lastPagePath: null,
      lastActivityAt: null,

      setSessionId: (sessionId) => {
        set({
          sessionId,
          sessionStartedAt: new Date().toISOString(),
          lastActivityAt: new Date().toISOString(),
        });
      },

      updateLastPage: (pagePath) => {
        set({
          lastPagePath: pagePath,
          lastActivityAt: new Date().toISOString(),
        });
      },

      updateLastActivity: () => {
        set({
          lastActivityAt: new Date().toISOString(),
        });
      },

      clearSession: () => {
        set({
          sessionId: null,
          sessionStartedAt: null,
          lastPagePath: null,
          lastActivityAt: null,
        });
      },
    }),
    {
      name: 'activity-session-storage',
      // ストレージから復元後、セッションの有効期限をチェック
      onRehydrateStorage: () => (state) => {
        if (state && isSessionExpired(state.sessionStartedAt)) {
          state.clearSession();
        }
      },
    }
  )
);

/**
 * 現在のセッションIDを取得（有効期限切れの場合はnull）
 */
export const getActiveSessionId = (): string | null => {
  const state = useActivityStore.getState();

  if (isSessionExpired(state.sessionStartedAt)) {
    state.clearSession();
    return null;
  }

  return state.sessionId;
};
